import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description?: string;
  canonicalPath?: string;
  keywords?: string;
  ogType?: 'website' | 'article';
  ogImage?: string;
  noIndex?: boolean;
  jsonLd?: Record<string, any>;
}

const DEFAULT_DESCRIPTION =
  'MannaPlag is an AI-powered plagiarism checker and AI content integrity platform for researchers, universities, publishers, institutions, and businesses.';
const BASE_URL = 'https://mannaplag.mannaanalytics.com';
const DEFAULT_IMAGE = `${BASE_URL}/og-image.png`;

export default function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  canonicalPath = '',
  keywords,
  ogType = 'website',
  ogImage = DEFAULT_IMAGE,
  noIndex = false,
  jsonLd,
}: SEOProps) {
  // Format title: if already contains 'MannaPlag', use as-is; otherwise append suffix
  const fullTitle = title.includes('MannaPlag') ? title : `${title} | MannaPlag`;

  // Compute clean canonical URL
  const cleanPath = canonicalPath.startsWith('/')
    ? canonicalPath
    : canonicalPath
    ? `/${canonicalPath}`
    : '';
  const canonicalUrl = `${BASE_URL}${cleanPath}`;

  return (
    <Helmet>
      {/* Title & Description */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />

      {/* Robots Directive */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large" />
      )}

      {/* Canonical */}
      {!noIndex && <link rel="canonical" href={canonicalUrl} />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content="MannaPlag" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Structured Data (JSON-LD) if provided */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}
